import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Meta from 'components/App/Meta';
import Pagination from 'components/Pagination';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Card,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
} from 'reactstrap';

import { getParamsFormObject, handleError } from 'utils';

import discoverCategory from 'api/discoverCategory';
import AppCloseModalButton from 'components/App/AppCloseModalButton';
import Loading from 'components/App/Loading';
import DiscoverCategoryDetails from 'components/DiscoverCategoryDetails';
import { toast } from 'react-toastify';
import styles from './ManagerDiscoveryCategories.module.scss';

const TITLE = 'Quản lý hạng mục';

const TAB = {
  ALL: 'ALL',
  PUBLISHED: 'true',
  UNPUBLISHED: 'false',
};

const NAV_TAB = [
  {
    title: 'Tất cả',
    value: TAB.ALL,
  },
  {
    title: 'Đã xuất bản',
    value: TAB.PUBLISHED,
  },
  {
    title: 'Chưa xuất bản',
    value: TAB.UNPUBLISHED,
  },
];

const breadcrumbList = [
  {
    name: 'Khám phá',
    isActive: false,
    link: '', // TODO: ADD ROUTE WHEN HAVE LINK
  },
  {
    name: TITLE,
    isActive: true,
    link: '',
  },
];

const DEFAULT_LIMIT = 20;

const getHref = (id) => `/manager-discovery/category/${id}`;

function ManagerDiscoveryCategories(props) {
  const {
    categoriesLoading,
    categories,
    actionGetDiscoveryCategories,
    actionCleanupDiscoveryCategories,
  } = props;

  const history = useHistory();
  const tableRef = React.useRef(null);

  const params = useMemo(() => new URLSearchParams(history.location.search),
    [history.location.search],
  );

  const searchQueries = React.useMemo(
    () => ({
      page: params.get('page') || 1,
      q: params.get('q') || '',
      limit: DEFAULT_LIMIT,
      isPublished: params.get('isPublished') || TAB.ALL,
    }),
    [params],
  );

  const [isOpenCreateCategory, setIsOpenCreateCategory] = React.useState(false);
  const [deleteCategory, setDeleteCategory] = React.useState({
    id: '',
    isOpen: false,
    isLoading: false,
  });
  const [search, setSearch] = React.useState(searchQueries.q);
  const [activeTab, setActiveTab] = React.useState(searchQueries.isPublished);

  const onPushSearchQueries = React.useCallback((queries) => {
    const formData = {
      ...searchQueries,
      ...queries,
    };

    history.push({ search: getParamsFormObject(formData) });

    // tableRef.current.scrollIntoView({ behavior: 'smooth' });

    actionGetDiscoveryCategories({
      ...formData,
      isPublished: formData.isPublished === TAB.ALL
        ? undefined
        : formData.isPublished === TAB.PUBLISHED,
    });
  }, [actionGetDiscoveryCategories, history, searchQueries]);

  const onChangePage = React.useCallback(
    (selected) => {
      onPushSearchQueries({ page: selected.selected + 1 });
    },
    [onPushSearchQueries],
  );

  const onCloseDeleteCategory = React.useCallback(() => {
    setDeleteCategory((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const onCloseCreateCategory = React.useCallback(() => {
    setIsOpenCreateCategory(false);
  }, []);

  const onSubmitDeleteCategory = React.useCallback(async () => {
    try {
      setDeleteCategory((prev) => ({ ...prev, isLoading: true }));

      await discoverCategory.deleteDiscoverCategory(deleteCategory.id);

      toast.success('Xóa danh mục thành công');

      if (deleteCategory.isOpen) {
        setDeleteCategory((prev) => ({
          ...prev,
          isOpen: false,
          id: '',
        }));
      }
      actionGetDiscoveryCategories({
        ...searchQueries,
        page: 1,
      });
    } catch (error) {
      handleError(error);
    } finally {
      setDeleteCategory((prev) => ({ ...prev, isLoading: false }));
    }
  }, [actionGetDiscoveryCategories, deleteCategory.id, deleteCategory.isOpen, searchQueries]);

  const totalPage = React.useMemo(
    () => Math.ceil((categories.total || 0) / DEFAULT_LIMIT),
    [categories.total],
  );

  React.useEffect(() => {
    actionGetDiscoveryCategories({
      ...searchQueries,
      isPublished: activeTab === TAB.ALL
        ? undefined
        : activeTab === TAB.PUBLISHED,
    });
  }, []);

  React.useEffect(() => () => {
    // CLEANUP ACTIONS
    actionCleanupDiscoveryCategories();
  }, [actionCleanupDiscoveryCategories]);

  const onChangeTab = React.useCallback((tab) => () => {
    setActiveTab(tab);
    onPushSearchQueries({ isPublished: tab, page: 1 });
  }, [onPushSearchQueries]);

  return (
    <>
      <div className={`page-content ${styles.Page}`}>
        <Meta name={TITLE} />

        <Container fluid>
          <Breadcrumb
            pageTitle={TITLE}
            breadcrumbList={breadcrumbList}
          />

          <Loading loading={categoriesLoading} loaderOnPage />

          <Card className="Section">
            <Row>
              <Col md="6">
                <Label className="Section__Search__Label">
                  Tìm kiếm
                </Label>

                <div className="position-relative">
                  <Input
                    placeholder="Tìm kiếm hạng mục"
                    style={{ paddingLeft: '36px' }}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        onPushSearchQueries({ q: search, page: 1 });
                      }
                    }}
                  />

                  <i className="fas fa-search Section__Search__Icon__Search" />
                </div>
              </Col>

              <Col md="6">
                <div className="d-flex align-items-end h-100 mb-24 mb-md-0">
                  <Button
                    onClick={() => onPushSearchQueries({ q: search, page: 1 })}
                    style={{ minWidth: '119px' }}
                  >
                    Tìm kiếm
                  </Button>
                </div>
              </Col>
            </Row>
          </Card>

          <Card className="Section">
            <div className="d-flex flex-column flex-md-row gap-4 justify-content-between custom_pills">
              <Nav pills>
                {
                  NAV_TAB.map((tab) => (
                    <NavItem key={tab.value}>
                      <NavLink
                        className={activeTab === tab.value ? 'active' : ''}
                        onClick={onChangeTab(tab.value)}
                      >
                        <span>{tab.title}</span>
                      </NavLink>
                    </NavItem>
                  ))
                }
              </Nav>

              <Button
                classes="Button__Add"
                fontSize="13px"
                onClick={() => setIsOpenCreateCategory(true)}
              >
                <i className="fas fa-plus" />
                Thêm hạng mục
              </Button>
            </div>

            <Label className="Table__Body__Total">
              {`${categories?.total || 0} danh mục`}
            </Label>

            <div ref={tableRef} className="w-100" style={{ overflow: 'auto' }}>
              <table className="table custom_table">
                <thead>
                  <tr>
                    <th>Hạng mục</th>
                    <th>Mô tả</th>
                    <th>Tổng điểm khám phá</th>
                    <th>Trạng thái</th>
                    <th>Tổng lượt đánh giá</th>
                  </tr>
                </thead>
                <tbody>
                  {categories?.data.map((category) => (
                    <tr key={category._id}>
                      <td>
                        <div className="d-flex flex-column gap-1 buttons-group">
                          <div>
                            {category.name}
                          </div>
                          <div>
                            <div className="buttons-action mt-2">
                              <button
                                type="button"
                                className="button-action button-destructive"
                                onClick={() => setDeleteCategory({
                                  id: category._id,
                                  isOpen: true,
                                })}
                              >
                                Xoá
                              </button>

                              <Link to={getHref(category._id)}>
                                <button
                                  type="button"
                                  className="button-action button-link"
                                >
                                  Xem chi tiết
                                </button>
                              </Link>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {category.description ?? '-'}
                      </td>
                      <td>{category.placeCount}</td>
                      <td>
                        <span className={
                          category.isPublished
                            ? 'text-success'
                            : 'text-danger'}
                        >
                          {category.isPublished ? 'Đã xuất bản' : 'Chưa xuất bản'}
                        </span>
                      </td>
                      <td>{category.reviewCount ?? 0}</td>
                    </tr>
                  ))}
                  {categories?.data.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center">
                        Không có dữ liệu
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Pagination
            page={searchQueries.page}
            pageCount={totalPage}
            onChangePage={onChangePage}
          />
        </Container>
      </div>

      <Modal
        isOpen={isOpenCreateCategory}
        toggle={onCloseCreateCategory}
        size="lg"
        fade
        style={{ maxWidth: '779px' }}
      >
        <ModalHeader
          toggle={onCloseCreateCategory}
          close={(
            <AppCloseModalButton
              onClick={onCloseCreateCategory}
              className="custom_button_close"
            />
          )
          }
        >
          Thêm hạng mục
        </ModalHeader>

        <ModalBody>
          <DiscoverCategoryDetails
            isCreate
            callback={() => {
              if (isOpenCreateCategory) setIsOpenCreateCategory(false);
              actionGetDiscoveryCategories({ ...searchQueries, page: 1 });
            }}
            isModal
            onCloseModal={onCloseCreateCategory}
          />
        </ModalBody>
      </Modal>

      <Modal
        isOpen={deleteCategory.isOpen}
        toggle={onCloseDeleteCategory}
        size="md"
        fade
      >
        <ModalHeader
          toggle={onCloseDeleteCategory}
          close={() => <AppCloseModalButton onClick={onCloseDeleteCategory} />}
        >
          Xóa hạng mục
        </ModalHeader>

        <ModalBody>Bạn có chắc chắn muốn xóa hạng mục này không?</ModalBody>

        <ModalFooter>
          <Button onClick={onCloseDeleteCategory} type="button" color="light">
            Hủy
          </Button>
          <Button
            onClick={onSubmitDeleteCategory}
            type="button"
            color="danger"
            disabled={deleteCategory.isLoading}
            loading={deleteCategory.isLoading}
          >
            Xóa
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

ManagerDiscoveryCategories.propTypes = {
  categoriesLoading: PropTypes.bool.isRequired,
  categories: PropTypes.instanceOf(Array).isRequired,
  actionGetDiscoveryCategories: PropTypes.func.isRequired,
  actionCleanupDiscoveryCategories: PropTypes.func.isRequired,
};

export default ManagerDiscoveryCategories;
